import { useCallback, useMemo, useState } from 'react'
import useChatTranscript from './engine/useChatTranscript'
import useMessageSearch from './engine/useMessageSearch'
import { makeGlobalChatApi } from './globalChatApi'
import { sendMessage } from '../../services/globalChatMessages'

// Global Search composition of the generic chat engine: a paginated transcript +
// message search + a `send` that adds an optimistic user turn while the assistant
// reply is generated. Mirrors useSearchroomChat; the only difference is the chat
// id and the send target (a whole-corpus search endpoint).
export default function useGlobalChat(chatId) {
  const api = useMemo(() => (chatId ? makeGlobalChatApi(chatId) : null), [chatId])

  const transcript = useChatTranscript(api)
  const search = useMessageSearch(api)

  const [sending, setSending] = useState(false)
  // Bumped to ask the transcript to snap to the bottom (on the user's own send).
  const [bottomSignal, setBottomSignal] = useState(0)

  const {
    hasMoreAfter,
    reset,
    appendLocal,
    replaceLocal,
    removeLocal,
    jumpTo,
    setError,
  } = transcript

  const send = useCallback(
    async (text, { model, guides } = {}) => {
      if (!chatId || sending) return
      setError('')
      setSending(true)

      // If the user is viewing older history, snap to the live end first so the
      // new turn is visible at the bottom.
      if (hasMoreAfter) await reset()

      const tempId = `temp-${Date.now()}`
      appendLocal({ _id: tempId, role: 'user', text, guides: guides || [], pending: true })
      setBottomSignal((n) => n + 1)

      try {
        const { userMessage, assistantMessage } = await sendMessage(chatId, text, {
          model,
          guides: (guides || []).map((g) => g._id),
        })
        replaceLocal(tempId, [userMessage, assistantMessage])
        setBottomSignal((n) => n + 1)
      } catch (err) {
        setError(err.message || 'Failed to send message')
        removeLocal(tempId)
      } finally {
        setSending(false)
      }
    },
    [chatId, sending, hasMoreAfter, reset, appendLocal, replaceLocal, removeLocal, setError]
  )

  // Jump the transcript to a searched message and close the results panel.
  const closeSearch = search.close
  const selectResult = useCallback(
    (id) => {
      closeSearch()
      jumpTo(id)
    },
    [closeSearch, jumpTo]
  )

  return {
    ...transcript,
    sending,
    send,
    bottomSignal,
    search,
    selectResult,
    resetKey: chatId || '',
  }
}
